import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Autocomplete
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  uploadImage,
  clearMedia,
} from "./../../actions/mediaActions";
import {
  fetchOneSubcategory,
  addEditSubCategory,
  deleteSubCategory,
  clearAllSubCategories,
} from "./../../actions/subcategoryActions";
import { fetchAllCategories } from "./../../actions/categoryActions";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import ImageContainerPreview from '../Common/ImageContainerPreview';
import SortableItemList from "../Common/SortableItemList";
import { INITIAL_LIST_STATE, createSortHandlers } from "../Common/sortableListUtils";
import MainContentContainer from "../MainContentContainer/MainContentContainer";
import { preventEnterFormSubmission } from '../../utils/formUtils';
import UniversalLinkField from '../Common/UniversalLinkField';

const AddEditSubCategory = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef();

  // Expose the submit handler to parent
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit({ preventDefault: () => { } });
    }
  }));

  const [state, setState] = useState({
    subcategory_id: null,
    subcategory_title: "",
    subcategory_description: "",
    subcategory_explore_term: "",
    subcategory_is_active: true,
    subcategory_is_quick_tap: false,
    subcategory_image: "",
    subcategory_image_preview: "",
    subcategory_grayscale_image: "",
    subcategory_grayscale_image_preview: "",
    category: 0,
    sessions: [],
    quotes: [],
    isSetToEdit: false,
    subcategory_is_new: false,
    subcategory_is_dark_mode: false,
    subcategory_deep_link_image: "",
    deep_link_image_preview: "",
  });

  // Add sessions state
  const [sessionsState, setSessionsState] = useState(INITIAL_LIST_STATE);

  // Add quick taps state
  const [quickTapsState, setQuickTapsState] = useState(INITIAL_LIST_STATE);

  // Add session data state
  const [sessionData, setSessionData] = useState({});

  const current_subcategory = useSelector(state => state.subcategories.current_subcategory);
  const isFetching = useSelector(state => state.subcategories.isFetching);
  const all_categories = useSelector(state => state.categories.all_categories);

  // Add session data handler
  const handleSessionDataChange = useCallback((id, data) => {
    setSessionData(prev => ({
      ...prev,
      [id]: {
        is_featured: data.is_featured ?? 0,
        is_dark_mode: data.is_dark_mode ?? 0
      }
    }));
  }, []);

  // Main data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Reset all state
        setState(prev => ({ ...prev, isSetToEdit: false }));
        dispatch(clearAllSubCategories());
        setSessionsState({ ...INITIAL_LIST_STATE, isLoading: true });
        setQuickTapsState({ ...INITIAL_LIST_STATE, isLoading: true });
        setSessionData({});

        await dispatch(fetchOneSubcategory(selectedId));
      } catch (error) {
        console.error('Error fetching subcategory:', error);
      }
    };

    if (isEdit) {
      fetchData();
    }

    // Cleanup function
    return () => {
      setState(prev => ({ ...prev, isSetToEdit: false }));
      dispatch(clearAllSubCategories());
      setSessionsState(INITIAL_LIST_STATE);
      setQuickTapsState(INITIAL_LIST_STATE);
      setSessionData({});
    };
  }, [dispatch, isEdit, selectedId]);

  // Fetch categories
  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);



  // Set subcategory for editing when data is loaded
  useEffect(() => {
    if (isEdit && current_subcategory && !state.isSetToEdit) {
      // Verify we're setting the correct subcategory
      if (current_subcategory.subcategory_id !== Number(selectedId)) {
        return;
      }

      let sessions = current_subcategory.sessions || [];
      let quick_taps = current_subcategory.quick_taps || [];

      var sortable_sessions = [...sessions.map(session => {
        return {
          session_name: session.session_name,
          session_id: session.session_id,
          session_is_featured: session.session_is_featured,
          session_is_dark_mode: session.session_is_dark_mode,
          subcategory_id: current_subcategory.subcategory_id
        };
      }), ...quick_taps.map(quick_tap => {
        return {
          session_name: quick_tap.title,
          session_id: quick_tap.id,
          session_is_featured: quick_tap.is_featured,
          session_is_dark_mode: quick_tap.is_dark_mode,
          subcategory_id: current_subcategory.subcategory_id
        };
      })];

      const newState = {
        subcategory_id: current_subcategory.subcategory_id,
        subcategory_title: current_subcategory.subcategory_title || '',
        subcategory_description: current_subcategory.subcategory_description || '',
        subcategory_explore_term: current_subcategory.subcategory_explore_term || '',
        subcategory_is_active: current_subcategory.subcategory_is_active ?? true,
        subcategory_is_quick_tap: !!current_subcategory.subcategory_is_quick_tap,
        subcategory_image: current_subcategory.subcategory_image || '',
        subcategory_image_preview: current_subcategory.subcategory_image || '',
        subcategory_grayscale_image: current_subcategory.subcategory_grayscale_image || '',
        subcategory_grayscale_image_preview: current_subcategory.subcategory_grayscale_image || '',
        category: current_subcategory.category_id || 0,
        sessions: sortable_sessions,
        quotes: current_subcategory.quotes || [],
        subcategory_is_new: current_subcategory.subcategory_is_new ?? false,
        subcategory_is_dark_mode: current_subcategory.subcategory_is_dark_mode ?? false,
        subcategory_deep_link_image: current_subcategory.subcategory_deep_link_image || '',
        universal_link: current_subcategory.universal_link || '',
        deep_link_image_preview: current_subcategory.subcategory_deep_link_image || '',
        isSetToEdit: true
      };

      setState(prev => ({
        ...prev,
        ...newState
      }));
    }
  }, [isEdit, current_subcategory, selectedId]);

  // Process sessions data from current subcategory
  useEffect(() => {
    if (!current_subcategory || !current_subcategory.sessions || state.subcategory_is_quick_tap) return;

    const sessionsItems = current_subcategory.sessions.map(obj => ({
      id: obj.session_id + "",
      label: obj.session_name,
      value: obj.session_id + ""
    }));

    const selectedSessionIds = current_subcategory.sessions.map(obj => obj.session_id + "");

    // Initialize sessionData with existing session data
    const initialSessionData = current_subcategory.sessions.reduce((acc, session) => {
      acc[session.session_id + ""] = {
        is_featured: session.session_is_featured || 0,
        is_dark_mode: session.session_is_dark_mode || 0
      };
      return acc;
    }, {});

    setSessionData(initialSessionData);
    setSessionsState({
      items: sessionsItems,
      isLoaded: true,
      sortOrder: selectedSessionIds,
      isLoading: false
    });
  }, [current_subcategory, state.subcategory_is_quick_tap]);

  // Process quick taps data from current subcategory
  useEffect(() => {
    if (!current_subcategory || !current_subcategory.quick_taps || !state.subcategory_is_quick_tap) return;

    const quickTapsItems = current_subcategory.quick_taps.map(obj => ({
      id: (obj.quick_tap_id || obj.id) + "",
      label: obj.quick_tap_title || obj.title,
      value: (obj.quick_tap_id || obj.id) + ""
    }));

    const selectedQuickTapIds = current_subcategory.quick_taps.map(obj => (obj.quick_tap_id || obj.id) + "");

    // Initialize sessionData with existing quick tap data
    const initialSessionData = current_subcategory.quick_taps.reduce((acc, quickTap) => {
      acc[(quickTap.quick_tap_id || quickTap.id) + ""] = {
        is_featured: quickTap.is_featured || false,
        is_dark_mode: quickTap.is_dark_mode || false
      };
      return acc;
    }, {});

    setSessionData(initialSessionData);
    setQuickTapsState({
      items: quickTapsItems,
      isLoaded: true,
      sortOrder: selectedQuickTapIds,
      isLoading: false
    });
  }, [current_subcategory, state.subcategory_is_quick_tap]);

  // Add sessions handlers
  const handleSessionsSort = useCallback((newItems) => {
    const updatedSessionData = { ...sessionData };
    newItems.forEach((id, index) => {
      if (updatedSessionData[id]) {
        updatedSessionData[id].sort_order = index;
      }
    });
    setSessionData(updatedSessionData);
    setSessionsState(prev => ({
      ...prev,
      sortOrder: [...newItems]
    }));
  }, [sessionData]);

  const handleSessionsRemove = useCallback((index) => {
    setSessionsState(prev => ({
      ...prev,
      sortOrder: prev.sortOrder.filter((_, i) => i !== index)
    }));
  }, []);



  // Quick taps sort/remove handlers via factory
  const quickTapsHandlers = useMemo(() => createSortHandlers(setQuickTapsState), []);



  // Handle quick tap switch change
  const handleQuickTapChange = useCallback((e) => {
    setState(prev => ({ ...prev, subcategory_is_quick_tap: e.target.checked }));
  }, []);

  const handleFieldChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (state.subcategory_image_preview) {
      window.URL.revokeObjectURL(state.subcategory_image_preview);
    }
    setState(prev => ({
      ...prev,
      subcategory_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          subcategory_image: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  const handleDeepLinkImageUpload = (e) => {
    const file = e.target.files[0];
    if (state.deep_link_image_preview) {
      window.URL.revokeObjectURL(state.deep_link_image_preview);
    }
    setState(prev => ({
      ...prev,
      deep_link_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          subcategory_deep_link_image: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  };

  // Update success handler in handleSubmit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!state.subcategory_title) {
      Swal.fire({
        icon: "error",
        text: "Subcategory name is required",
        showConfirmButton: true,
        showCancelButton: false,
        confirmButtonText: "Ok",
      });
      return;
    }

    // Show loading state
    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    let {
      quotes: quotes_json,
      category: category_id,
      ...subcategory_json
    } = state;

    quotes_json = quotes_json.filter((obj) => obj.quote_text !== "");

    // Parse sessions or quick taps based on subcategory type
    const session_ids_json = state.subcategory_is_quick_tap
      ? quickTapsState.sortOrder.map(id => ({
        session_id: id,
        session_is_featured: sessionData[id]?.is_featured || 0,
        session_is_dark_mode: sessionData[id]?.is_dark_mode || 0
      }))
      : sessionsState.sortOrder.map(id => ({
        session_id: id,
        session_is_featured: sessionData[id]?.is_featured || 0,
        session_is_dark_mode: sessionData[id]?.is_dark_mode || 0
      }));

    const options = {
      quotes_json: JSON.stringify(quotes_json),
      category_id: category_id,
      subcategory_json: JSON.stringify(subcategory_json),
      session_ids_json: JSON.stringify(session_ids_json),
    };

    dispatch(addEditSubCategory(options))
      .then(() => {
        dispatch(clearAllSubCategories());
        setSessionsState(INITIAL_LIST_STATE);
        setQuickTapsState(INITIAL_LIST_STATE);
        setSessionData({});
        navigate('/subcategories');
      })
      .catch((error) => {
        console.error('Error saving subcategory:', error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to save subcategory",
          showConfirmButton: true,
          confirmButtonText: "Ok",
        });
      });
  };

  const handleDelete = (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "warning",
      text: "Are you sure you want to delete this subcategory?",
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete Subcategory",
    }).then((result) => {
      if (result.value) {
        dispatch(deleteSubCategory(state.subcategory_id, () => navigate('/subcategories')));
      }
    });
  };

  const removeQuote = useCallback((index) => {
    setState(prev => ({
      ...prev,
      quotes: prev.quotes.filter((_, i) => i !== index)
    }));
  }, []);

  const addQuote = useCallback((e) => {
    e.preventDefault();
    setState(prev => ({
      ...prev,
      quotes: [...prev.quotes, { speaker: "", quote_text: "" }]
    }));
  }, []);

  return (
    <>
      <LoadingBackdrop open={isEdit && isFetching} />
      <MainContentContainer component="form" ref={formRef} onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission} autoComplete="off">
        <Box display="grid" gap={4} sx={{ paddingTop: 2 }}>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            <Box>
              <TextField
                fullWidth
                label="Subcategory Name"
                name="subcategory_title"
                value={state.subcategory_title}
                onChange={handleFieldChange}
                required
              />
            </Box>
            <Box>
              <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        name="subcategory_is_active"
                        checked={!!state.subcategory_is_active}
                        onChange={(e) => setState(prev => ({ ...prev, subcategory_is_active: e.target.checked }))}
                      />
                    }
                    label="Active"
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        name="subcategory_is_new"
                        checked={!!state.subcategory_is_new}
                        onChange={(e) => setState(prev => ({ ...prev, subcategory_is_new: e.target.checked }))}
                      />
                    }
                    label="Is New"
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        name="subcategory_is_dark_mode"
                        checked={!!state.subcategory_is_dark_mode}
                        onChange={(e) => setState(prev => ({ ...prev, subcategory_is_dark_mode: e.target.checked }))}
                      />
                    }
                    label="Dark Mode"
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        name="subcategory_is_quick_tap"
                        checked={!!state.subcategory_is_quick_tap}
                        onChange={handleQuickTapChange}
                      />
                    }
                    label="Quick Tap"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            <Box>
              <Autocomplete
                fullWidth
                options={React.useMemo(() => {
                  if (!all_categories) return [];

                  // Get unique categories by category_id to prevent duplicates
                  const uniqueCategories = Object.values(all_categories).reduce((acc, category) => {
                    const existingCategory = acc.find(c => c.category_id === category.category_id);
                    if (!existingCategory) {
                      acc.push(category);
                    }
                    return acc;
                  }, []);

                  return uniqueCategories.filter(category =>
                    !!category.category_is_quick_tap === !!state.subcategory_is_quick_tap &&
                    !category.category_is_audiobook
                  );
                }, [all_categories, state.subcategory_is_quick_tap])}
                getOptionLabel={(option) => {
                  let label = option.category_title || '';
                  if (option.category_is_quick_tap) {
                    label += ' (Quick Tap)';
                  }
                  if (!option.category_is_active) {
                    label += ' (Inactive)';
                  }
                  return label;
                }}
                isOptionEqualToValue={(option, value) => option.category_id === value.category_id}
                value={all_categories && state.category ? Object.values(all_categories).find(cat => cat.category_id === state.category) || null : null}
                onChange={(_, newValue) => {
                  setState(prev => ({ ...prev, category: newValue ? newValue.category_id : 0 }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Category" required />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.category_id}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      opacity: !option.category_is_active ? 0.6 : 1,
                      fontStyle: !option.category_is_active ? 'italic' : 'normal'
                    }}>
                      {option.category_title}
                      {option.category_is_quick_tap > 0 && (
                        <Box component="span" sx={{ ml: 1, fontSize: '0.8em', color: 'primary.main' }}>
                          (Quick Tap)
                        </Box>
                      )}
                      {!option.category_is_active && (
                        <Box component="span" sx={{ ml: 1, fontSize: '0.8em', color: 'warning.main' }}>
                          (Inactive)
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Explore More Search Term"
                name="subcategory_explore_term"
                value={state.subcategory_explore_term}
                onChange={handleFieldChange}
              />
            </Box>
          </Box>

          <Box>
            <TextField
              fullWidth
              label="Description"
              name="subcategory_description"
              value={state.subcategory_description}
              onChange={handleFieldChange}
              multiline
              rows={2}
            />
          </Box>
          {/* Subcategory Image and Share Settings side by side */}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 2fr' }} gap={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Subcategory Image
              </Typography>
              <ImageContainerPreview
                title="Subcategory Image"
                image={state.subcategory_image_preview || state.subcategory_image}
                name="subcategory_image"
                handleRemove={() => setState(prev => ({ ...prev, subcategory_image: "", subcategory_image_preview: null }))}
                handleImageUpload={handleImageUpload}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>
                Share Settings
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                <Box>
                  <ImageContainerPreview
                    title="Deep Link Image"
                    image={state.deep_link_image_preview || state.subcategory_deep_link_image}
                    name="deep_link_image"
                    handleRemove={() => setState(prev => ({ ...prev, subcategory_deep_link_image: "", deep_link_image_preview: null }))}
                    handleImageUpload={handleDeepLinkImageUpload}
                    showCopyUrlButton={true}
                    sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
                  />
                </Box>
                <Box>
                  <UniversalLinkField url={state.universal_link} />
                </Box>
              </Box>
            </Box>
          </Box>

          {isEdit && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {state.subcategory_is_quick_tap ? 'Quick Taps' : 'Sessions'}
              </Typography>
              {state.subcategory_is_quick_tap ? (
                quickTapsState.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <SortableItemList
                    items={quickTapsState.items}
                    sortOrder={quickTapsState.sortOrder}
                    onRemove={quickTapsHandlers.onRemove}
                    onSortEnd={quickTapsHandlers.onSort}
                    showSwitches={true}
                    onSessionDataChange={handleSessionDataChange}
                    sessionData={sessionData}
                  />
                )
              ) : (
                sessionsState.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <SortableItemList
                    items={sessionsState.items}
                    sortOrder={sessionsState.sortOrder}
                    onRemove={handleSessionsRemove}
                    onSortEnd={handleSessionsSort}
                    showSwitches={true}
                    onSessionDataChange={handleSessionDataChange}
                    sessionData={sessionData}
                  />
                )
              )}
            </Box>
          )}

          <Box>
            <Typography variant="h6" gutterBottom>
              Quotes
            </Typography>
            {state.quotes.map((quote, index) => (
              <Box key={index} display="flex" gap={2} sx={{ mb: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Quote Text"
                  value={quote.quote_text}
                  onChange={(e) => {
                    setState(prev => ({
                      ...prev,
                      quotes: prev.quotes.map((q, i) =>
                        i === index ? { ...q, quote_text: e.target.value } : q
                      )
                    }));
                  }}
                  multiline
                  rows={1}
                />
                <Tooltip title="Remove Quote">
                  <IconButton
                    color="error"
                    onClick={() => removeQuote(index)}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button
              variant="contained"
              onClick={addQuote}
              sx={{ mt: 2 }}
            >
              Add Quote
            </Button>
          </Box>

          {isEdit && (
            <Box sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Delete Subcategory
              </Button>
            </Box>
          )}
        </Box>
      </MainContentContainer>
    </>
  );
});

export default AddEditSubCategory;
